/** Line icons on a 24px grid, drawn in the current text colour. */
import type { ReactNode, SVGProps } from "react";

type IconProps = SVGProps<SVGSVGElement> & { size?: number };

function icon(name: string, children: ReactNode) {
  const Icon = ({ size = 22, ...props }: IconProps) => (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      {...props}
    >
      {children}
    </svg>
  );
  Icon.displayName = name;
  return Icon;
}

export const PinIcon = icon(
  "PinIcon",
  <>
    <path d="M12 21s-7-6.1-7-11.2A7 7 0 0 1 19 9.8C19 14.9 12 21 12 21z" />
    <circle cx="12" cy="9.8" r="2.6" />
  </>,
);
export const FlagIcon = icon(
  "FlagIcon",
  <>
    <path d="M5 21V4" />
    <path d="M5 4h11l-2 4 2 4H5" />
  </>,
);
export const ClockIcon = icon(
  "ClockIcon",
  <>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7v5l3 2" />
  </>,
);
export const UserIcon = icon(
  "UserIcon",
  <>
    <circle cx="12" cy="8" r="4" />
    <path d="M4 21c0-4 3.6-6 8-6s8 2 8 6" />
  </>,
);
export const UsersIcon = icon(
  "UsersIcon",
  <>
    <circle cx="9" cy="8" r="3.5" />
    <path d="M2.5 20c0-3.5 3-5.5 6.5-5.5s6.5 2 6.5 5.5" />
    <path d="M16 4.6a3.5 3.5 0 0 1 0 6.8" />
    <path d="M18 14.8c2.2.7 3.5 2.4 3.5 5.2" />
  </>,
);
export const BagIcon = icon(
  "BagIcon",
  <>
    <rect x="5" y="7" width="14" height="13" rx="2" />
    <path d="M9 7V5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
    <path d="M9 11v5M15 11v5" />
  </>,
);
export const CardIcon = icon(
  "CardIcon",
  <>
    <rect x="2.5" y="5.5" width="19" height="13" rx="2" />
    <path d="M2.5 10h19M6.5 15h3" />
  </>,
);
export const CashIcon = icon(
  "CashIcon",
  <>
    <rect x="2.5" y="6" width="19" height="12" rx="2" />
    <circle cx="12" cy="12" r="2.5" />
    <path d="M6 9.5v5M18 9.5v5" />
  </>,
);
export const BriefcaseIcon = icon(
  "BriefcaseIcon",
  <>
    <rect x="3" y="7" width="18" height="13" rx="2" />
    <path d="M9 7V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2M3 12.5h18" />
  </>,
);
export const PhoneIcon = icon(
  "PhoneIcon",
  <path d="M5.5 3.5h3l1.5 4.5-2.2 1.3a11 11 0 0 0 4.9 4.9l1.3-2.2 4.5 1.5v3a2 2 0 0 1-2.2 2A16.5 16.5 0 0 1 3.5 5.7a2 2 0 0 1 2-2.2z" />,
);
export const ChevronRight = icon("ChevronRight", <path d="M9 5l7 7-7 7" />);
export const ChevronLeft = icon("ChevronLeft", <path d="M15 5l-7 7 7 7" />);
export const CloseIcon = icon("CloseIcon", <path d="M6 6l12 12M18 6L6 18" />);
export const SearchIcon = icon(
  "SearchIcon",
  <>
    <circle cx="11" cy="11" r="7" />
    <path d="M20 20l-3.6-3.6" />
  </>,
);
export const LocateIcon = icon("LocateIcon", <path d="M3.5 11.2L20.5 3.5l-7.7 17-1.9-7.4z" />);
export const HomeIcon = icon(
  "HomeIcon",
  <>
    <path d="M3.5 11L12 3.8l8.5 7.2" />
    <path d="M5.5 9.5V20h4.5v-5.5h4V20h4.5V9.5" />
  </>,
);
export const StarIcon = icon(
  "StarIcon",
  <path d="M12 3.5l2.6 5.4 5.9.8-4.3 4.1 1 5.8-5.2-2.8-5.2 2.8 1-5.8-4.3-4.1 5.9-.8z" />,
);
export const PlusIcon = icon("PlusIcon", <path d="M12 5v14M5 12h14" />);
export const MinusIcon = icon("MinusIcon", <path d="M5 12h14" />);
export const CheckIcon = icon("CheckIcon", <path d="M4.5 12.5l5 5 10-11" />);
export const ReceiptIcon = icon(
  "ReceiptIcon",
  <>
    <path d="M6 3h12v18l-3-2-3 2-3-2-3 2z" />
    <path d="M9 8h6M9 12h6" />
  </>,
);
export const BellIcon = icon(
  "BellIcon",
  <>
    <path d="M6 9a6 6 0 0 1 12 0c0 6 2.5 7.5 2.5 7.5h-17S6 15 6 9z" />
    <path d="M10 20a2.2 2.2 0 0 0 4 0" />
  </>,
);
export const LogOutIcon = icon(
  "LogOutIcon",
  <>
    <path d="M14 4h4a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2h-4" />
    <path d="M10 16l-4-4 4-4M6 12h10" />
  </>,
);
export const TrashIcon = icon(
  "TrashIcon",
  <>
    <path d="M4 7h16M10 11v6M14 11v6" />
    <path d="M6 7l1 13h10l1-13M9 7V4h6v3" />
  </>,
);
export const DownloadIcon = icon(
  "DownloadIcon",
  <>
    <path d="M12 4v11M7.5 10.5L12 15l4.5-4.5" />
    <path d="M5 20h14" />
  </>,
);
export const InfoIcon = icon(
  "InfoIcon",
  <>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 11v5M12 7.5v.5" />
  </>,
);
export const AlertIcon = icon(
  "AlertIcon",
  <>
    <path d="M12 3.5L2.5 20h19z" />
    <path d="M12 10v4.5M12 17.5v.3" />
  </>,
);
export const ListIcon = icon(
  "ListIcon",
  <>
    <rect x="4" y="3.5" width="16" height="17" rx="2" />
    <path d="M8 8.5h8M8 12h8M8 15.5h5" />
  </>,
);
export const CarIcon = icon(
  "CarIcon",
  <>
    <path d="M3.5 16.5v-4l2-5a2 2 0 0 1 1.9-1.3h9.2a2 2 0 0 1 1.9 1.3l2 5v4" />
    <path d="M2.5 12.5h19v4.5H2.5z" />
    <circle cx="7" cy="17.5" r="1.8" />
    <circle cx="17" cy="17.5" r="1.8" />
  </>,
);
export const EditIcon = icon("EditIcon", <path d="M4 20h4L19 9l-4-4L4 16zM13.5 6.5l4 4" />);
export const MailIcon = icon(
  "MailIcon",
  <>
    <rect x="3" y="5" width="18" height="14" rx="2" />
    <path d="M3.5 6l8.5 7 8.5-7" />
  </>,
);
export const ShieldIcon = icon(
  "ShieldIcon",
  <path d="M12 3l7.5 3v5.5c0 4.6-3.2 8.2-7.5 9.5-4.3-1.3-7.5-4.9-7.5-9.5V6z" />,
);
export const RepeatIcon = icon(
  "RepeatIcon",
  <>
    <path d="M4 12a7 7 0 0 1 12-4.9L18 9" />
    <path d="M18 4v5h-5M20 12a7 7 0 0 1-12 4.9L6 15" />
    <path d="M6 20v-5h5" />
  </>,
);
export const WalletIcon = icon(
  "WalletIcon",
  <>
    <rect x="3" y="6.5" width="18" height="13" rx="2.5" />
    <path d="M6 6.5l9-3.2 1.4 3.2" />
    <path d="M21 11.5h-3.5a1.8 1.8 0 0 0 0 3.6H21" />
  </>,
);
export const NavigateIcon = icon("NavigateIcon", <path d="M12 2.8l7.5 18.2-7.5-4.3-7.5 4.3z" />);
export const PowerIcon = icon(
  "PowerIcon",
  <>
    <path d="M12 3v8" />
    <path d="M6.6 6.6a7.5 7.5 0 1 0 10.8 0" />
  </>,
);
export const PauseIcon = icon("PauseIcon", <path d="M8.5 5v14M15.5 5v14" />);
export const PlayIcon = icon("PlayIcon", <path d="M7 4.8v14.4L19 12z" />);
